import React, { useEffect, useMemo, useRef, useState } from 'react';
import { nlt } from '../services/api';
import type { AgentConfiguratorState } from '../types/agentConfigurator';
import type { NltPatchReviewState } from '../types/nltPatchReview';
import { capitalizeError, getErrorMessage } from '../utils/errorUtils';
import {
  allFieldsAccepted,
  buildConfigFieldDiffs,
  defaultFieldDecisions,
  type ConfigFieldDiff,
} from '../utils/nltConfigFieldDiff';
import {
  buildNltUserMessageWithQuote,
  chatMessageRoleLabel,
  formatNltCompareQuoteLabel,
  type NltCompareQuote,
} from '../utils/nltQuote';
import {
  formatOptimizationDatasetDisplay,
  normalizeOptimizationDatasetNames,
} from '../utils/optimizationDatasetName';
import { buildNltGeneratePatchRequest } from '../utils/nltPatchRequest';
import { resolveNltAdminConfigForApi, resolveNltAdminConfigId } from '../utils/nltAdminConfigPayload';
import { buildNltWorkflowPatchRequest } from '../utils/nltWorkflowRequest';
import { approxJsonBytes, formatBytes } from '../utils/nltWorkflowProjection';
import {
  appliedStateIds,
  buildProposalViews,
  summarizeProposals,
  type ProposalView,
} from '../utils/nltWorkflowProposals';
import type { WorkflowEditBridge } from '../types/workflowBridge';
import OptimizationDatasetPicker from './OptimizationDatasetPicker';
import './OptimizationDatasetPicker.css';
import NLTPatchSummaryWidget from './NLTPatchSummaryWidget';
import NLTWorkflowProposals from './NLTWorkflowProposals';
import ChatMarkdownBody from './ui/ChatMarkdownBody';
import './NLTSidebar.css';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  patchRunId?: string;
  includeInDialog?: boolean;
  patchFieldDiffs?: ConfigFieldDiff[];
  /** Workflow mode: every operation the agent proposed this turn. */
  workflowViews?: ProposalView[];
}

/**
 * `502` on this route means the ML step did not answer (see WORKFLOW_API.md §"Ошибки
 * самой ручки"), and on the live graph the usual reason is the size of the request —
 * the whole graph plus the whole product config runs about a megabyte. Say that instead
 * of the generic "service unavailable", and name the lever.
 */
function describeWorkflowPatchError(error: unknown, sentBytes: number, sentGlobals: boolean): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 502) {
    const size = sentBytes > 0 ? ` The request was ${formatBytes(sentBytes)}.` : '';
    const lever = sentGlobals
      ? ' Try turning off “Send global sections” below — they cannot be written from this connector anyway.'
      : '';
    return `The tuning model did not answer (502).${size}${lever}`;
  }
  return getErrorMessage(error, 'Failed to generate a workflow patch');
}

/** What the agent says when it patched the workflow but wrote no prose. */
function assistantMessageForWorkflowPatch(views: ProposalView[], backendContent?: string): string {
  const trimmed = backendContent?.trim();
  if (trimmed) return trimmed;
  const stats = summarizeProposals(views);
  if (stats.total === 0) return 'No workflow changes were proposed.';
  if (stats.applied === 0) return `Proposed ${stats.total} operation${stats.total === 1 ? '' : 's'}, none of which applied.`;
  return `Applied ${stats.applied} of ${stats.total} proposed operation${stats.total === 1 ? '' : 's'}.`;
}

function assistantMessageForPatch(fieldDiffs: ConfigFieldDiff[], backendContent?: string): string {
  const trimmed = backendContent?.trim();
  if (trimmed) return trimmed;

  if (fieldDiffs.length === 0) {
    return 'No configuration changes were suggested.';
  }
  return `Suggested ${fieldDiffs.length} change${fieldDiffs.length === 1 ? '' : 's'}. Review the summary below.`;
}

interface NLTSidebarProps {
  integrationId: string | null;
  /**
   * `config` patches the agent config via `/nlt/patch`; `workflow` patches the target
   * system's state graph via `/nlt/workflow-patch`. Driven by the open Connector tab.
   */
  mode?: 'config' | 'workflow';
  /** Present in workflow mode: how to read the snapshot and hand edits back to the graph. */
  workflowBridge?: WorkflowEditBridge | null;
  connectorConfig?: AgentConfiguratorState | null;
  patchReview: NltPatchReviewState | null;
  onPatchReviewChange: (state: NltPatchReviewState | null) => void;
  onFocusConfigurator?: () => void;
  onApplyPatch: () => void | Promise<void>;
  onRejectPatch: () => void | Promise<void>;
  focusedPatchPath?: string | null;
  onNavigatePatchField?: (path: string) => void;
  pendingQuote?: NltCompareQuote | null;
  onClearQuote?: () => void;
  onClose: () => void;
  datasets?: string[];
  selectedDatasets?: string[];
  onDatasetsChange?: (names: string[]) => void;
  datasetsLoading?: boolean;
}

const NLTSidebar: React.FC<NLTSidebarProps> = ({
  integrationId,
  mode = 'config',
  workflowBridge = null,
  connectorConfig,
  patchReview,
  onPatchReviewChange,
  onFocusConfigurator,
  onApplyPatch,
  onRejectPatch,
  focusedPatchPath = null,
  onNavigatePatchField,
  pendingQuote = null,
  onClearQuote,
  onClose,
  datasets = [],
  selectedDatasets = [],
  onDatasetsChange,
  datasetsLoading = false,
}) => {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [patchNavByMessageId, setPatchNavByMessageId] = useState<Record<string, number>>({});
  const [datasetsExpanded, setDatasetsExpanded] = useState(false);
  const [datasetsHidden, setDatasetsHidden] = useState(false);
  /**
   * Contract default: send the whole `product_config`. Turning it off keeps only
   * `default_agents` — the rest is unwritable here anyway (403), and on the live graph
   * it is ~40% of a ~1 MB request that the ML step answers with 502.
   */
  const [sendGlobals, setSendGlobals] = useState(true);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);

  const pendingRun = patchReview?.pendingRun ?? null;
  const changedFieldCount = patchReview?.fieldDiffs.length ?? 0;
  const latestPatchMessageId = useMemo(() => {
    for (let i = chatMessages.length - 1; i >= 0; i -= 1) {
      const message = chatMessages[i];
      if (message.patchFieldDiffs?.length) return message.id;
    }
    return null;
  }, [chatMessages]);
  const patchBulkHidden =
    Boolean(patchReview) &&
    changedFieldCount > 0 &&
    allFieldsAccepted(patchReview!.fieldDiffs, patchReview!.fieldDecisions);
  const isWorkflowMode = mode === 'workflow';
  // The panel publishes cheap facts about the graph; the projection itself (~450 KB) is
  // only built at send time, never on render.
  const workflowWarnings = isWorkflowMode ? workflowBridge?.warnings ?? [] : [];
  const canChat = isWorkflowMode
    ? Boolean(integrationId && workflowBridge)
    : Boolean(integrationId && connectorConfig);
  const datasetsSummary = useMemo(() => {
    if (datasetsLoading) return 'Loading…';
    if (selectedDatasets.length === 0) {
      return datasets.length === 0 ? 'No datasets available' : 'None selected';
    }
    return formatOptimizationDatasetDisplay(selectedDatasets) || `${selectedDatasets.length} selected`;
  }, [datasetsLoading, selectedDatasets, datasets.length]);
  const canGeneratePatch =
    canChat &&
    (Boolean(inputText.trim()) || Boolean(pendingQuote?.text.trim()));

  const composerPlaceholder = !canChat
    ? isWorkflowMode
      ? 'Load a workflow to start…'
      : 'Load a config to start…'
    : pendingQuote?.text.trim()
      ? 'What should change about this reply?'
      : isWorkflowMode
        ? 'e.g. Soften the tone of the greeting state…'
        : 'e.g. Make prompts shorter…';

  useEffect(() => {
    setChatMessages([]);
    setInputText('');
    setError(null);
    setPatchNavByMessageId({});
    setDatasetsExpanded(false);
    setDatasetsHidden(false);
    onPatchReviewChange(null);
    // Reset on integration change, and when switching between config and workflow tuning:
    // the two dialogs talk about different documents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, mode]);

  useEffect(() => {
    const chatEl = chatScrollRef.current;
    if (!chatEl) return;
    chatEl.scrollTop = chatEl.scrollHeight;
  }, [chatMessages, pendingRun?.id, generating]);

  useEffect(() => {
    if (!pendingQuote?.text.trim()) return;
    composerInputRef.current?.focus();
  }, [pendingQuote?.text, pendingQuote?.source]);

  async function handleGenerateWorkflow() {
    const composed = buildNltUserMessageWithQuote(inputText, pendingQuote);
    const bridge = workflowBridge;
    if (!integrationId || !bridge || !composed.trim()) return;

    // Re-read the projection at send time: the panel may have republished since render,
    // and sending a stale graph is how you get a turn full of addressing errors.
    const projection = bridge.getProjection({ includeGlobals: sendGlobals });
    if (!projection) {
      setError('No workflow snapshot is loaded yet.');
      return;
    }

    const datasetNames =
      normalizeOptimizationDatasetNames(selectedDatasets).length > 0 ? selectedDatasets : datasets;
    const userText = composed;
    onClearQuote?.();
    setInputText('');
    setGenerating(true);
    setError(null);
    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', content: userText };
    const priorMessages = chatMessages;
    setChatMessages((prev) => [...prev, userMsg]);

    const rollbackFailedUserMessage = () => {
      setChatMessages((prev) => prev.filter((message) => message.id !== userMsg.id));
      setInputText(userText);
    };

    // `nlt_config_id` is optional here — the backend falls back to the latest admin
    // config on its own, so a lookup failure is not worth failing the turn over.
    const nltConfigId = await resolveNltAdminConfigId();
    const sentBytes = approxJsonBytes(projection);

    try {
      const res = await nlt.generateWorkflowPatch(
        integrationId,
        buildNltWorkflowPatchRequest({
          projection,
          priorMessages,
          latestHuman: userText,
          baseRevision: bridge.baseRevision,
          nltConfigId,
          datasetNames,
        })
      );

      const views = buildProposalViews(res.proposals || [], projection, res.proposed_workflow ?? null);
      const stats = summarizeProposals(views);
      const messages: ChatMessage[] = [
        {
          id: `wa-${Date.now()}`,
          role: 'assistant',
          content: assistantMessageForWorkflowPatch(views, res.content),
          includeInDialog: true,
          workflowViews: views.length > 0 ? views : undefined,
        },
      ];

      // The backend never writes: staging the proposed states as local edits hands the
      // change to the panel's own dry-run → apply flow, which owns the 409 handling.
      if (stats.appliedStates > 0 && res.proposed_workflow?.states) {
        const touched = appliedStateIds(views);
        bridge.applyProposedStates(res.proposed_workflow.states);
        bridge.focusWorkflow();
        messages.push({
          id: `sys-${Date.now()}`,
          role: 'assistant',
          includeInDialog: false,
          content:
            `Loaded into the graph as unsaved edits (${touched.join(', ')}). ` +
            'Press “Save…” on the Workflow tab to dry-run and apply.',
        });
      } else if (stats.appliedGlobals > 0) {
        messages.push({
          id: `sys-${Date.now()}`,
          role: 'assistant',
          includeInDialog: false,
          content:
            'Every applied change lands in a global section, and this connector only accepts writes to `workflow` — ' +
            'nothing was staged for saving.',
        });
      }

      setChatMessages((prev) => [...prev, ...messages]);
    } catch (e: unknown) {
      rollbackFailedUserMessage();
      setError(describeWorkflowPatchError(e, sentBytes, sendGlobals));
    } finally {
      setGenerating(false);
      setTimeout(() => composerInputRef.current?.focus(), 0);
    }
  }

  async function handleGenerate() {
    if (isWorkflowMode) return handleGenerateWorkflow();
    const composed = buildNltUserMessageWithQuote(inputText, pendingQuote);
    if (!integrationId || !connectorConfig || !composed.trim()) return;
    const datasetNames =
      normalizeOptimizationDatasetNames(selectedDatasets).length > 0
        ? selectedDatasets
        : datasets;
    const userText = composed;
    onClearQuote?.();
    setInputText('');
    setGenerating(true);
    setError(null);
    onPatchReviewChange(null);
    setPatchNavByMessageId({});
    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', content: userText };
    const priorMessages = chatMessages;
    setChatMessages((prev) => [...prev, userMsg]);

    const baseConfig = connectorConfig;

    const rollbackFailedUserMessage = () => {
      setChatMessages((prev) => prev.filter((message) => message.id !== userMsg.id));
      setInputText(userText);
    };

    let nltAdmin;
    try {
      nltAdmin = await resolveNltAdminConfigForApi();
    } catch {
      rollbackFailedUserMessage();
      setGenerating(false);
      setError('Failed to load NLT admin config.');
      return;
    }

    const patchRequest = buildNltGeneratePatchRequest({
      connectorConfig: baseConfig,
      nltAdmin,
      priorMessages,
      latestHuman: userText,
      datasetNames,
    });

    try {
      const res = await nlt.generatePatch(integrationId, patchRequest);
      if (res.success && res.data) {
        const proposedConfig = res.data.proposed_config as AgentConfiguratorState;
        const fieldDiffs = buildConfigFieldDiffs(baseConfig, proposedConfig);
        const summary = assistantMessageForPatch(fieldDiffs, res.data.content);

        if (fieldDiffs.length > 0) {
          onPatchReviewChange({
            pendingRun: res.data,
            baseConfig,
            proposedConfig,
            fieldDiffs,
            fieldDecisions: defaultFieldDecisions(fieldDiffs),
          });
          onFocusConfigurator?.();
          const assistantMsgId = `a-${res.data!.id}`;
          onNavigatePatchField?.(fieldDiffs[0].path);
          setPatchNavByMessageId({ [assistantMsgId]: 0 });
        }

        setChatMessages((prev) => [
          ...prev,
          {
            id: `a-${res.data!.id}`,
            role: 'assistant',
            content: summary,
            patchRunId: res.data!.id,
            includeInDialog: true,
            patchFieldDiffs: fieldDiffs.length > 0 ? fieldDiffs : undefined,
          },
        ]);
      } else {
        rollbackFailedUserMessage();
        setError(capitalizeError(res.error?.message || 'Failed to generate patch'));
      }
    } catch (e: unknown) {
      rollbackFailedUserMessage();
      setError(getErrorMessage(e, 'Failed to generate patch'));
    } finally {
      setGenerating(false);
      setTimeout(() => composerInputRef.current?.focus(), 0);
    }
  }

  function handleApplyWithMessage() {
    onApplyPatch();
    setChatMessages((prev) => [
      ...prev,
      {
        id: `sys-${Date.now()}`,
        role: 'assistant',
        content: 'Changes applied in the configurator. Use Save when you are ready to persist.',
        includeInDialog: false,
      },
    ]);
  }

  function handleRejectWithMessage() {
    onRejectPatch();
    setChatMessages((prev) => [
      ...prev,
      { id: `sys-${Date.now()}`, role: 'assistant', content: 'Patch rejected.', includeInDialog: false },
    ]);
  }

  function handlePatchNavIndexChange(messageId: string, index: number) {
    setPatchNavByMessageId((prev) => ({ ...prev, [messageId]: index }));
  }

  function handlePatchNavigate(messageId: string, path: string) {
    onFocusConfigurator?.();
    onNavigatePatchField?.(path);
    setPatchNavByMessageId((prev) => {
      const message = chatMessages.find((item) => item.id === messageId);
      if (!message?.patchFieldDiffs?.length) return prev;
      const index = message.patchFieldDiffs.findIndex((diff) => diff.path === path);
      if (index < 0) return prev;
      return { ...prev, [messageId]: index };
    });
  }

  return (
    <aside className="nlt-sidebar-panel" aria-label="NLT chat">
      <header className="nlt-panel__header">
        <div className="nlt-panel__title">
          <i className="fas fa-magic" aria-hidden="true"></i>
          <span>NLT</span>
          {isWorkflowMode ? <span className="nlt-panel__mode">Workflow</span> : null}
        </div>
        <button
          type="button"
          className="nlt-panel__close"
          onClick={onClose}
          aria-label="Close NLT panel"
          title="Close"
        >
          <i className="fas fa-times" aria-hidden="true"></i>
        </button>
      </header>

      {!integrationId ? (
        <div className="nlt-panel__empty">
          <p>Select an integration to use NLT.</p>
        </div>
      ) : !canChat ? (
        <div className="nlt-panel__empty">
          <p>
            {isWorkflowMode
              ? 'Load the workflow graph on the Workflow tab before chatting with NLT.'
              : 'Load a config on the Integrations tab before chatting with NLT.'}
          </p>
        </div>
      ) : (
        <>
          {error && (
            <div className="nlt-panel__error">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {error}
            </div>
          )}

          {isWorkflowMode && workflowWarnings.length > 0 && (
            <div className="nlt-panel__warn">
              <i className="fas fa-exclamation-triangle" aria-hidden="true" />
              <ul>
                {workflowWarnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="nlt-panel__chat" ref={chatScrollRef}>
            {chatMessages.length === 0 && !pendingRun && (
              <p className="nlt-panel__chat-placeholder">
                {isWorkflowMode ? (
                  <>
                    Describe what to change in the dialogue graph, then press Generate. The proposal is staged as
                    unsaved edits — you still apply it with “Save…” on the Workflow tab.
                  </>
                ) : (
                  'Describe what to change in the current config, then press Generate.'
                )}
              </p>
            )}
            {chatMessages.map((msg) => {
              const canFocusConfigurator =
                Boolean(patchReview) && msg.id === latestPatchMessageId;
              const navIndex = patchNavByMessageId[msg.id] ?? 0;

              return (
                <div key={msg.id} className={`nlt-bubble nlt-bubble--${msg.role}`}>
                  <div className="nlt-bubble__label">{chatMessageRoleLabel(msg.role)}</div>
                  <div className="nlt-bubble__text">
                    {msg.role === 'assistant' ? (
                      <ChatMarkdownBody>{msg.content}</ChatMarkdownBody>
                    ) : (
                      msg.content
                    )}
                  </div>
                  {msg.patchFieldDiffs && msg.patchFieldDiffs.length > 0 ? (
                    <NLTPatchSummaryWidget
                      fieldDiffs={msg.patchFieldDiffs}
                      activeIndex={navIndex}
                      canFocusConfigurator={canFocusConfigurator}
                      onIndexChange={(index) => handlePatchNavIndexChange(msg.id, index)}
                      onNavigate={(path) => handlePatchNavigate(msg.id, path)}
                    />
                  ) : null}
                  {msg.workflowViews ? <NLTWorkflowProposals views={msg.workflowViews} /> : null}
                </div>
              );
            })}
            {generating && (
              <div className="nlt-bubble nlt-bubble--assistant nlt-bubble--loading">
                <div className="nlt-bubble__label">{chatMessageRoleLabel('assistant')}</div>
                <div className="nlt-bubble__typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </div>

          {pendingRun &&
            pendingRun.status === 'pending' &&
            patchReview &&
            changedFieldCount > 0 &&
            !patchBulkHidden && (
              <div className="nlt-panel__review-hint">
                <p>
                  <strong>{changedFieldCount}</strong> field{changedFieldCount === 1 ? '' : 's'} changed — review in
                  the configurator. Only changed fragments are highlighted (red&nbsp;=&nbsp;removed, green&nbsp;=&nbsp;added).
                </p>
                <button
                  type="button"
                  className="nlt-panel__btn nlt-panel__btn--link"
                  onClick={() => onFocusConfigurator?.()}
                >
                  Go to configurator →
                </button>
                <div className="nlt-panel__patch-actions">
                  <button
                    type="button"
                    className="nlt-panel__btn nlt-panel__btn--accept"
                    onClick={() => handleApplyWithMessage()}
                  >
                    Apply changes
                  </button>
                  <button
                    type="button"
                    className="nlt-panel__btn nlt-panel__btn--reject"
                    onClick={() => handleRejectWithMessage()}
                  >
                    Reject all
                  </button>
                </div>
              </div>
            )}

          <footer className="nlt-panel__composer">
            {isWorkflowMode && workflowBridge ? (
              <div className="nlt-panel__payload">
                <div className="nlt-panel__payload-head">
                  <span>
                    Payload · <strong>{formatBytes(workflowBridge.stateBytes + (sendGlobals ? workflowBridge.globalBytes : 0))}</strong>
                  </span>
                  <span className="nlt-panel__payload-split">
                    {workflowBridge.stateCount} states {formatBytes(workflowBridge.stateBytes)}
                    {sendGlobals ? ` · ${workflowBridge.globalCount} globals ${formatBytes(workflowBridge.globalBytes)}` : ''}
                  </span>
                </div>
                <label className="nlt-panel__payload-toggle">
                  <input type="checkbox" checked={sendGlobals} onChange={(e) => setSendGlobals(e.target.checked)} />
                  <span>
                    Send global sections
                    <span className="nlt-panel__payload-hint">
                      {sendGlobals
                        ? 'Off keeps only default_agents — the rest cannot be written from this connector (403).'
                        : `Sending default_agents only. Global edits will not be addressable.`}
                    </span>
                  </span>
                </label>
              </div>
            ) : null}
            {datasetsHidden ? (
              <button
                type="button"
                className="nlt-panel__datasets-restore"
                onClick={() => {
                  setDatasetsHidden(false);
                  setDatasetsExpanded(false);
                }}
              >
                <i className="fas fa-database" aria-hidden="true" />
                <span>Datasets</span>
                <span className="nlt-panel__datasets-restore-meta">{datasetsSummary}</span>
              </button>
            ) : (
              <div className={`nlt-panel__datasets${datasetsExpanded ? ' nlt-panel__datasets--expanded' : ''}`}>
                <div className="nlt-panel__datasets-head">
                  <button
                    type="button"
                    className="nlt-panel__datasets-head-main"
                    onClick={() => setDatasetsExpanded((v) => !v)}
                    aria-expanded={datasetsExpanded}
                  >
                    <i
                      className={`fas fa-chevron-${datasetsExpanded ? 'down' : 'right'} nlt-panel__datasets-chevron`}
                      aria-hidden="true"
                    />
                    <span className="nlt-panel__datasets-label">Datasets</span>
                    {selectedDatasets.length > 0 ? (
                      <span className="nlt-panel__datasets-count">{selectedDatasets.length} selected</span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    className="nlt-panel__datasets-hide"
                    title="Hide datasets"
                    aria-label="Hide datasets"
                    onClick={() => setDatasetsHidden(true)}
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                </div>
                {!datasetsExpanded ? (
                  <p className="nlt-panel__datasets-summary">{datasetsSummary}</p>
                ) : (
                  <>
                    <OptimizationDatasetPicker
                      datasets={datasets}
                      selected={selectedDatasets}
                      onChange={(names) => onDatasetsChange?.(names)}
                      disabled={generating || !canChat}
                      loading={datasetsLoading}
                      emptyMessage="No datasets for this integration. Import on the Datasets tab."
                      className="nlt-panel__dataset-picker"
                    />
                    <p className="nlt-panel__datasets-hint">
                      Selected datasets are sent as <code>dataset_name</code> (top level and inside{' '}
                      <code>config</code>) — string or string[].
                    </p>
                  </>
                )}
              </div>
            )}
            {pendingQuote?.text.trim() ? (
              <div className="nlt-panel__quote-chip">
                <div className="nlt-panel__quote-chip-head">
                  <span className="nlt-panel__quote-chip-label">
                    Quote · {formatNltCompareQuoteLabel(pendingQuote)}
                  </span>
                  <button
                    type="button"
                    className="nlt-panel__quote-chip-clear"
                    onClick={() => onClearQuote?.()}
                    aria-label="Remove quote"
                    title="Remove quote"
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                </div>
                <p className="nlt-panel__quote-chip-text">{pendingQuote.text.trim()}</p>
              </div>
            ) : null}
            <div className="nlt-panel__composer-controls">
              <textarea
                ref={composerInputRef}
                className="nlt-panel__composer-input"
                placeholder={composerPlaceholder}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (!generating && canGeneratePatch) void handleGenerate();
                  }
                }}
                disabled={!canChat || generating}
                rows={2}
              />
              <button
                type="button"
                className="nlt-panel__composer-send"
                onClick={() => void handleGenerate()}
                disabled={!canGeneratePatch || generating}
                aria-label="Generate patch"
              >
                {generating ? (
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                ) : (
                  <>
                    <i className="fas fa-magic" aria-hidden="true" />
                    <span className="nlt-panel__composer-send-label">Generate</span>
                  </>
                )}
              </button>
            </div>
          </footer>
        </>
      )}
    </aside>
  );
};

export default NLTSidebar;
